import { getCookie, setCookie } from "@remarkablenet/rcm-utils/cookies";
import { disableScroll, enableScroll, gtag } from "../utility-functions/utility-functions";

type Consent = "granted" | "denied";
type ConsentKeys =
    | "ad_storage"
    | "ad_user_data"
    | "ad_personalization"
    | "analytics_storage"
    | "functionality_storage"
    | "personalization_storage"
    | "security_storage";

type ConsentOptions = {
    [key in ConsentKeys]?: Consent;
};

const DEFAULT_CONSENT: ConsentOptions = {
    ad_storage: "denied",
    analytics_storage: "denied",
    functionality_storage: "denied",
    personalization_storage: "denied",
    ad_personalization: "denied",
    ad_user_data: "denied",
    security_storage: "denied",
};

const waitForExponea = (callback: () => void) => {
    if (window.exponea && typeof window.exponea.start === "function") {
        callback();
    } else {
        setTimeout(() => waitForExponea(callback), 100);
    }
};


export const initConsentChecks = () => {
    const currentPath: string = window.location.pathname;

    addConsentEventListeners();

    if (!hasConsented() && currentPath != "/privacy-policy" && currentPath != "/cookie-policy") {
        showConsentModal();
    }
};

export const hasConsented = () => {
    var permissions = getConsentCookie();

    if (permissions && permissions.personalization_storage === "granted") {
        waitForExponea(() => {
            window.exponea.start();
        });
    }

    return !!permissions;
};

const showConsentModal = () => {
    var consentModal = document.querySelector<HTMLElement>(".consent-mode");
    var permissions = getConsentCookie();

    if (permissions) {
        var getAnalytics = document.querySelector<HTMLInputElement>("#consent_analytics");
        var getPersonalise = document.querySelector<HTMLInputElement>("#consent_personalise");

        getAnalytics.checked = permissions.analytics_storage === "granted";
        getPersonalise.checked = permissions.personalization_storage === "granted";
    }

    disableScroll();
    consentModal.classList.remove("hidden");
};

const hideConsentModal = () => {
    var consentModal = document.querySelector<HTMLElement>(".consent-mode");

    consentModal.classList.add("hidden");
    enableScroll();
};

const setConsentCookie = (permissions: ConsentOptions) => {
    setCookie({ name: "consent_acceptance", value: JSON.stringify(permissions), days: 365 });
};

export const getConsentCookie = () => {
    const permissions = JSON.parse(getCookie({ name: "consent_acceptance" }));
    return permissions as ConsentOptions;
};

export const setConsent = (type: "default" | "update", permissions: ConsentOptions, setCookie = true) => {
    gtag("consent", type, permissions);

    if (type === "update" && setCookie) {
        setConsentCookie(permissions);
    }

    if (type === "update" && permissions.analytics_storage === "granted") {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "consent_update",
            analytics_storage: permissions.analytics_storage || "denied",
            personalization_storage: permissions.personalization_storage || "denied",
        });
    }

    if (type === "update" && permissions.functionality_storage === "granted" && permissions.security_storage === "granted") {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "strictly_necessary_consent"
        });
    }

    if (type === "update" && permissions.ad_personalization === "granted" && permissions.ad_user_data === "granted" && permissions.personalization_storage === "granted") {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "personalisation_consent"
        });
    }

    if (type === "update" && permissions.analytics_storage === "granted") {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: "analytics_consent"
        });
    }

    if (type === "update") {
        pushConsentLoad(permissions);
    }
};

export const setDefaultConsent = () => {
    setConsent("default", DEFAULT_CONSENT);
};

export const pushConsentLoad = (permissions: ConsentOptions = DEFAULT_CONSENT) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: "consent_load",
        ad_storage: permissions.ad_storage || "denied",
        ad_user_data: permissions.ad_user_data || "denied",
        ad_personalization: permissions.ad_personalization || "denied",
        analytics_storage: permissions.analytics_storage || "denied",
        functionality_storage: permissions.functionality_storage || "denied",
        personalization_storage: permissions.personalization_storage || "denied",
        security_storage: permissions.security_storage || "denied",
    });
};

const acceptAllConsent = () => {
    setConsent("update", {
        ad_storage: "granted",
        ad_personalization: "granted",
        ad_user_data: "granted",
        functionality_storage: "granted",
        analytics_storage: "granted",
        personalization_storage: "granted",
        security_storage: "granted",
    });

    waitForExponea(() => {
        window.exponea.start();
    });
};

const rejectNonEssentialConsent = () => {
    setConsent("update", {
        ad_storage: "denied",
        ad_personalization: "denied",
        ad_user_data: "denied",
        functionality_storage: "granted",
        analytics_storage: "denied",
        personalization_storage: "denied",
        security_storage: "granted",
    });
};

const addConsentEventListeners = () => {
    document.querySelectorAll(".trigger-cookie-popup").forEach((el) => {
        el.addEventListener("click", () => {
            showConsentModal();
        });
    });

    document.querySelector(".consent-mode--accept").addEventListener("click", () => {
        acceptAllConsent();
        hideConsentModal();
    });

    document.querySelector(".consent-mode--manage").addEventListener("click", () => {
        document.querySelector(".consent-mode--overview").classList.add("hidden");
        document.querySelector(".consent-mode--manager").classList.remove("hidden");
    });

    document.querySelector(".consent-mode--reject").addEventListener("click", () => {
        rejectNonEssentialConsent();
        hideConsentModal();
    });

    document.querySelector(".consent-mode--save").addEventListener("click", () => {
        var getNecessary = document.querySelector<HTMLInputElement>("#consent_necessary");
        var getAnalytics = document.querySelector<HTMLInputElement>("#consent_analytics");
        var getPersonalise = document.querySelector<HTMLInputElement>("#consent_personalise");

        var necessary: Consent = getNecessary.checked ? "granted" : "denied";
        var analytics: Consent = getAnalytics.checked ? "granted" : "denied";
        var personalise: Consent = getPersonalise.checked ? "granted" : "denied";

        setConsent("update", {
            functionality_storage: necessary,
            security_storage: necessary,
            ad_personalization: personalise,
            ad_user_data: personalise,
            personalization_storage: personalise,
            ad_storage: personalise,
            analytics_storage: analytics,
        });

        if (personalise === "granted") {
            waitForExponea(() => {
                window.exponea.start();
            });
        }

        hideConsentModal();
    });
};
